import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import cpeHistoryService from '../services/cpeHistoryService';
import type {
  CreditRecord,
  EducationCategory,
  ThirdPartyCategoryCreditInput,
  ThirdPartyCreditFormData,
} from '../services/cpeHistoryService';

const DELIVERY_METHODS = [
  'Group Live',
  'Group Internet-Based',
  'Self-Study',
  'Nano Learning',
  'Blended Learning',
  'Instructed',
  'Professional Journals',
  'Published Materials',
  'Reviewer',
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';

interface ThirdPartyCreditDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editRecord?: CreditRecord;
  categories: EducationCategory[];
}

export default function ThirdPartyCreditDialog({
  open,
  onClose,
  onSaved,
  editRecord,
  categories,
}: ThirdPartyCreditDialogProps) {
  const isEdit = !!editRecord;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState<ThirdPartyCreditFormData>({
    activityTitle: '',
    providerName: '',
    nasbaSponsorId: '',
    deliveryMethod: '',
    categoryCredits: [{ educationCategoryId: 0, creditHours: 1 }],
    dateEarned: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  // Raw text per credit-hours input so decimals can be typed (e.g. "2.")
  // without the controlled number value clobbering intermediate states.
  const [creditHoursText, setCreditHoursText] = useState<string[]>(['1']);
  const [certificate, setCertificate] = useState<File | undefined>();
  const [removeCertificate, setRemoveCertificate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingGroup, setLoadingGroup] = useState(false);
  const [error, setError] = useState('');

  // Reset form when dialog opens
  useEffect(() => {
    if (!open) return;
    setError('');
    setCertificate(undefined);
    setRemoveCertificate(false);

    const initializeForCreate = () => {
      setForm({
        activityTitle: '',
        providerName: '',
        nasbaSponsorId: '',
        deliveryMethod: '',
        categoryCredits: [{ educationCategoryId: 0, creditHours: 1 }],
        dateEarned: new Date().toISOString().slice(0, 10),
        notes: '',
      });
      setCreditHoursText(['1']);
    };

    const thirdPartyCreditId = editRecord?.thirdPartyCreditId;

    if (!thirdPartyCreditId) {
      initializeForCreate();
      return;
    }

    setLoadingGroup(true);
    cpeHistoryService.getThirdPartyCreditGroup(thirdPartyCreditId)
      .then((group) => {
        const categoryCredits = group.credits
          .filter((c) => c.educationCategoryId != null)
          .map((c) => ({
            id: c.id,
            educationCategoryId: c.educationCategoryId ?? 0,
            creditHours: c.creditHours,
          }));

        setForm({
          activityTitle: group.activityTitle ?? '',
          providerName: group.providerName ?? '',
          nasbaSponsorId: group.nasbaSponsorId ?? '',
          deliveryMethod: group.deliveryMethod ?? '',
          categoryCredits: categoryCredits.length > 0
            ? categoryCredits
            : [{ educationCategoryId: 0, creditHours: 1 }],
          dateEarned: group.dateEarned.slice(0, 10),
          notes: group.notes ?? '',
        });
        setCreditHoursText(
          categoryCredits.length > 0
            ? categoryCredits.map((c) => String(c.creditHours))
            : ['1'],
        );
      })
      .catch(() => {
        setError('Failed to load external credit details. Please try again.');
        initializeForCreate();
      })
      .finally(() => {
        setLoadingGroup(false);
      });
  }, [open, editRecord]);

  function updateField<K extends keyof ThirdPartyCreditFormData>(key: K, value: ThirdPartyCreditFormData[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function updateCategoryCredit(index: number, patch: Partial<ThirdPartyCategoryCreditInput>) {
    setForm((prev) => ({
      ...prev,
      categoryCredits: prev.categoryCredits.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)),
    }));
  }

  function updateCreditHours(index: number, text: string) {
    setCreditHoursText((prev) => prev.map((t, i) => (i === index ? text : t)));
    updateCategoryCredit(index, { creditHours: parseFloat(text) || 0 });
  }

  function addCategoryCreditRow() {
    setForm((prev) => ({
      ...prev,
      categoryCredits: [...prev.categoryCredits, { educationCategoryId: 0, creditHours: 1 }],
    }));
    setCreditHoursText((prev) => [...prev, '1']);
  }

  function removeCategoryCreditRow(index: number) {
    setForm((prev) => ({
      ...prev,
      categoryCredits: prev.categoryCredits.filter((_, i) => i !== index),
    }));
    setCreditHoursText((prev) => prev.filter((_, i) => i !== index));
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be less than 10MB');
      return;
    }
    setError('');
    setCertificate(file);
    setRemoveCertificate(false);
  }

  function handleRemoveCertificate() {
    setCertificate(undefined);
    setRemoveCertificate(true);
  }

  async function handleSave() {
    if (!form.activityTitle.trim()) {
      setError('Activity title is required');
      return;
    }
    if (form.categoryCredits.length === 0) {
      setError('At least one education category is required');
      return;
    }

    const hasInvalidCategory = form.categoryCredits.some((entry) => !entry.educationCategoryId || entry.educationCategoryId <= 0);
    if (hasInvalidCategory) {
      setError('Select an education category for each row');
      return;
    }

    const hasDuplicateCategory = new Set(form.categoryCredits.map((entry) => entry.educationCategoryId)).size !== form.categoryCredits.length;
    if (hasDuplicateCategory) {
      setError('Each education category can only be used once');
      return;
    }

    const hasInvalidCredits = form.categoryCredits.some((entry) => !entry.creditHours || entry.creditHours <= 0);
    if (hasInvalidCredits) {
      setError('Credit hours must be greater than 0 for each category');
      return;
    }

    // Credits are earned in tenths of an hour — validate on the raw text to
    // avoid IEEE 754 false rejections from float arithmetic.
    const hasInvalidPrecision = creditHoursText
      .slice(0, form.categoryCredits.length)
      .some((text) => !/^\d+(\.\d)?$/.test(text.trim()));
    if (hasInvalidPrecision) {
      setError('Credit hours must be in increments of 0.1 (e.g. 1.5)');
      return;
    }
    if (!form.dateEarned) {
      setError('Date earned is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const creditGroupId = editRecord?.creditGroupId;

      if (isEdit && creditGroupId) {
        await cpeHistoryService.updateThirdPartyCredit(
          creditGroupId,
          form,
          certificate,
          removeCertificate,
        );
      } else {
        await cpeHistoryService.createThirdPartyCredit(form, certificate);
      }
      onSaved();
    } catch {
      setError(isEdit ? 'Failed to update credit. Please try again.' : 'Failed to add credit. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const existingCertUrl = isEdit ? editRecord?.certificateUrl : undefined;
  const hasCert = (existingCertUrl && !removeCertificate) || certificate;
  const totalCreditHours = form.categoryCredits.reduce((sum, entry) => sum + (entry.creditHours || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{isEdit ? 'Edit external credit' : 'Add external credit'}</DialogTitle>
      <DialogContent>
        {loadingGroup ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Provenance for spreadsheet-imported credits: a line, not a chip — these are ordinary editable credits */}
          {isEdit && editRecord?.importedAt && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Imported {editRecord.importedByFirm ? 'by your firm' : 'from spreadsheet'} on{' '}
              {new Date(editRecord.importedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </Typography>
          )}

          <TextField
            label="Activity Title"
            value={form.activityTitle}
            onChange={(e) => updateField('activityTitle', e.target.value)}
            fullWidth
            required
            placeholder="e.g. Annual Tax Update Conference"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Provider / Sponsor Name"
              value={form.providerName}
              onChange={(e) => updateField('providerName', e.target.value)}
              fullWidth
              placeholder="e.g. AICPA"
            />
            <TextField
              label="Sponsor ID"
              value={form.nasbaSponsorId}
              onChange={(e) => updateField('nasbaSponsorId', e.target.value)}
              sx={{ minWidth: 160 }}
              placeholder="e.g. 108737"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Delivery Method"
              value={form.deliveryMethod ?? ''}
              onChange={(e) => updateField('deliveryMethod', e.target.value || undefined)}
              fullWidth
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {DELIVERY_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Stack spacing={1.5}>
            <Typography variant="overline" component="div" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
              Education categories and credits
            </Typography>

            {form.categoryCredits.map((entry, index) => (
              <Box key={`${index}-${entry.educationCategoryId}`} sx={{ display: 'flex', gap: 1.25, alignItems: 'center' }}>
                <TextField
                  select
                  label="Education Category"
                  value={entry.educationCategoryId || ''}
                  onChange={(e) => updateCategoryCredit(index, { educationCategoryId: Number(e.target.value) || 0 })}
                  fullWidth
                  required
                >
                  <MenuItem value="">
                    <em>Select category</em>
                  </MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Credit Hours"
                  type="number"
                  value={creditHoursText[index] ?? ''}
                  onChange={(e) => updateCreditHours(index, e.target.value)}
                  required
                  slotProps={{ htmlInput: { min: 1, step: 1, inputMode: 'decimal' } }}
                  sx={{ width: 140 }}
                />

                <IconButton
                  aria-label="Remove category"
                  onClick={() => removeCategoryCreditRow(index)}
                  disabled={form.categoryCredits.length === 1}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            ))}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button startIcon={<AddIcon />} onClick={addCategoryCreditRow} sx={{ alignSelf: 'flex-start' }}>
                Add category
              </Button>

              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Total: {totalCreditHours.toFixed(1)} credits
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Date Earned"
              type="date"
              value={form.dateEarned}
              onChange={(e) => updateField('dateEarned', e.target.value)}
              required
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ minWidth: 170 }}
            />
          </Box>

          <TextField
            label="Notes"
            value={form.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            fullWidth
            multiline
            minRows={2}
            placeholder="Optional details about this credit"
          />

          {/* Certificate file upload */}
          <Box>
            <Typography
              variant="overline"
              component="div"
              sx={{ color: 'text.secondary', lineHeight: 1.5, mb: 1 }}
            >
              Certificate (optional)
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                sx={{ fontWeight: 500 }}
              >
                {hasCert ? 'Replace file' : 'Upload file'}
                <input
                  type="file"
                  hidden
                  accept={ACCEPTED_FILE_TYPES}
                  onChange={handleFileChange}
                />
              </Button>
              {certificate && (
                <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                  {certificate.name}
                </Typography>
              )}
              {existingCertUrl && !removeCertificate && !certificate && (
                <>
                  <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'status.completed.fg', fontWeight: 500 }}>
                    Certificate uploaded
                  </Typography>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<CloseIcon />}
                    onClick={handleRemoveCertificate}
                    sx={{ minWidth: 'auto', py: 0 }}
                  >
                    Remove
                  </Button>
                </>
              )}
            </Box>
            <Typography variant="caption" component="div" sx={{ color: 'text.disabled', mt: 0.5 }}>
              PDF, JPG, or PNG (10 MB max)
            </Typography>
          </Box>
        </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={
            saving
            || loadingGroup
            || !form.activityTitle.trim()
            || form.categoryCredits.length === 0
            || !form.dateEarned
          }
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
        >
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
